import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
} from '@mui/material';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import Flex from '@Components/Bases/Flex';
import { FormInput } from '@Components/Form';
import { disabledFormInputStyles } from '@Components/Form/styles';
import {
  IIncomeStructureFormValues,
  IIncomeCategoryFormValues,
} from '@Router/NatureOfBusiness/Containers/NatureOfBusinessFormProvider/types';
import { useNatureOfBusinessForm } from '@Router/NatureOfBusiness/Containers/NatureOfBusinessFormProvider';
import {
  INCOME_CATEGORY_TOPIC,
  INCOME_SUBGROUP_CATEGORY,
} from 'src/Enum/IncomeStructure';
import { OtherIncomeModalSchema } from './validators';
import {
  mapIncomeCategoryToFormValues,
  updateIncomeCategory,
} from '../../utils';
import { createNewExtendedOtherIncome } from './utils';

type OtherIncomeModalProps = {
  subgroup: INCOME_SUBGROUP_CATEGORY;
  mode: 'create' | 'edit';
  open: boolean;
  close: () => void;
  keyItem?: string;
  editData?: IIncomeCategoryFormValues;
};

const OtherIncomeModal = ({
  subgroup,
  open,
  close,
  mode,
  keyItem,
  editData,
}: OtherIncomeModalProps) => {
  const initialValue =
    mode === 'edit' ? mapIncomeCategoryToFormValues(editData) : undefined;

  const otherIncomeModalContext = useForm<IIncomeCategoryFormValues>({
    defaultValues: initialValue,
    mode: 'onChange',
    resolver: zodResolver(OtherIncomeModalSchema(subgroup)),
  });

  const { handleSubmit, reset, formState, getValues } = otherIncomeModalContext;
  const { control, setValue } = useNatureOfBusinessForm();

  const incomeStructure = useWatch({
    control,
    name: 'incomeStructure',
  }) as IIncomeStructureFormValues;

  const otherIncomeItems = incomeStructure?.otherIncomeItems || {};
  const { isCompletedTh = false, isCompletedEn = false } =
    incomeStructure?.completedSection ?? {};

  const topic =
    subgroup === INCOME_SUBGROUP_CATEGORY.OTHER_OPERATION_INCOME
      ? INCOME_CATEGORY_TOPIC.OTHER_INCOME_FROM_OPERATIONS
      : INCOME_CATEGORY_TOPIC.OTHER_INCOME_NOT_FROM_OPERATIONS;

  const handleClose = () => {
    reset();
    close();
  };

  const handleSave = () => {
    const formValues = getValues();

    if (mode === 'create') {
      const prevExtendedItems = Object.values(otherIncomeItems).filter(
        (item) => item.incomeCategory?.subGroup === subgroup
      );
      const nextSequence =
        prevExtendedItems.reduce(
          (max, item) => Math.max(max, item.incomeCategory?.sequence ?? 0),
          0
        ) + 1;
      const newItemKey = `${subgroup}-${nextSequence}`;
      const newExtendedItem = createNewExtendedOtherIncome(
        formValues,
        subgroup,
        nextSequence
      );

      setValue(
        `incomeStructure.otherIncomeItems.${newItemKey}`,
        newExtendedItem
      );
    }

    if (mode === 'edit' && keyItem) {
      const prevExtendedData = otherIncomeItems[keyItem].incomeCategory;
      const updateExtendedItem = updateIncomeCategory(
        formValues,
        prevExtendedData
      );

      setValue(
        `incomeStructure.otherIncomeItems.${keyItem}.incomeCategory`,
        updateExtendedItem
      );
    }

    handleClose();
  };

  return (
    <FormProvider {...otherIncomeModalContext}>
      <Dialog open={open} maxWidth="md">
        <DialogTitle fontWeight={800} fontSize={16}>
          {mode === 'create' ? `เพิ่ม${topic}` : `แก้ไข${topic}`}
        </DialogTitle>
        <DialogContent>
          <Flex flexDirection={'column'} gap={2} mt={1}>
            <FormInput
              name="nameTh"
              label={`ชื่อ${topic} (ไทย)`}
              placeholder={`กรุณาระบุชื่อ${topic} (ไทย)`}
              required
              sx={isCompletedTh ? disabledFormInputStyles : undefined}
            />
            <FormInput
              name="nameEn"
              label={`ชื่อ${topic} (อังกฤษ)`}
              placeholder={`กรุณาระบุชื่อ${topic} (อังกฤษ)`}
              required
              sx={isCompletedEn ? disabledFormInputStyles : undefined}
            />
          </Flex>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button color="inherit" onClick={handleClose}>
            ยกเลิก
          </Button>
          <Button
            onClick={handleSubmit(handleSave)}
            disabled={!formState.isValid}
          >
            ยืนยัน
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

export default OtherIncomeModal;
